import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import WeatherSlide from '../components/WeatherSlide';
import AlertBar from '../components/AlertBar';
import ZoneLayout from '../components/ZoneLayout';
import useFullyKiosk from '../hooks/useFullyKiosk';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default content - always included in slideshow rotation
const getDefaultContent = (weatherEnabled = true, showAd = true) => {
  const items = [];
  if (showAd) {
    items.push({ 
      type: 'image', 
      url: `${window.location.origin}/frostbyte_signage_ad.png` 
    });
  }
  if (weatherEnabled) {
    items.push({ type: 'weather' });
  }
  return items;
};

// Helper function to get full URL for uploaded files
const getFullUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

// Helper function to convert YouTube URLs to embed format
const getYouTubeEmbedUrl = (url) => {
  if (!url) return url;
  if (url.includes('/embed/')) return url;
  
  let videoId = null;
  const watchMatch = url.match(/[?&]v=([^&]+)/);
  if (watchMatch) videoId = watchMatch[1];
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) videoId = shortMatch[1];
  const vMatch = url.match(/youtube\.com\/v\/([^?&]+)/);
  if (vMatch) videoId = vMatch[1];
  
  if (videoId) return `https://www.youtube.com/embed/${videoId}`;
  return url;
};

const isYouTubeUrl = (url) => {
  return url && (url.includes('youtube.com') || url.includes('youtu.be'));
};

const BkrmmnPage = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [imageProgress, setImageProgress] = useState(0);
  const [pageEnabled, setPageEnabled] = useState(true);
  const [orientation, setOrientation] = useState('landscape');
  const [weatherZipCode, setWeatherZipCode] = useState(null);
  const [weatherEnabled, setWeatherEnabled] = useState(true);
  const [slideDuration, setSlideDuration] = useState(5);
  const [showDefaultAd, setShowDefaultAd] = useState(true);
  const [alertText, setAlertText] = useState(null);
  const [alertEnabled, setAlertEnabled] = useState(false);
  const [zoneLayout, setZoneLayout] = useState(false);
  const [zoneClock, setZoneClock] = useState(true);
  const [zoneTicker, setZoneTicker] = useState('');
  const [displayId] = useState(() => {
    let id = localStorage.getItem('frostbyte_display_id_bkrmmn');
    if (!id) {
      id = 'display_bkrmmn_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
      localStorage.setItem('frostbyte_display_id_bkrmmn', id);
    }
    return id;
  });
  useFullyKiosk(displayId);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const videoContainerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  
  // Auto-refresh page every 1 hour - waits for video to finish
  useEffect(() => {
    const ONE_HOUR = 60 * 60 * 1000;
    const refreshTimer = setTimeout(() => {
      const currentItem = content[currentIndex];
      if (currentItem?.type === 'video') {
        setPendingRefresh(true);
      } else {
        window.location.reload();
      }
    }, ONE_HOUR);
    return () => clearTimeout(refreshTimer);
  }, [content, currentIndex]);
  
  // Health check-in
  useEffect(() => {
    const getBrowserInfo = () => {
      const ua = navigator.userAgent;
      let browser = 'Unknown';
      let os = 'Unknown';
      if (ua.includes('Chrome')) browser = 'Chrome';
      else if (ua.includes('Firefox')) browser = 'Firefox';
      else if (ua.includes('Safari')) browser = 'Safari';
      else if (ua.includes('Edge')) browser = 'Edge';
      if (ua.includes('Windows')) os = 'Windows';
      else if (ua.includes('Mac')) os = 'macOS';
      else if (ua.includes('Linux')) os = 'Linux';
      else if (ua.includes('Android')) os = 'Android';
      else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
      return { browser, os };
    };
    
    const getConnectionType = () => {
      if (navigator.connection) {
        return navigator.connection.effectiveType || navigator.connection.type || 'Unknown';
      }
      return 'Unknown';
    };
    
    const sendHeartbeat = async () => {
      try {
        const { browser, os } = getBrowserInfo();
        await axios.post(`${API}/health/check-in`, {
          display_id: displayId,
          page_id: 'bkrmmn',
          user_agent: navigator.userAgent,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          orientation: window.screen.orientation?.type || (window.innerWidth > window.innerHeight ? 'landscape' : 'portrait'),
          connection_type: getConnectionType(),
          browser,
          os,
          current_item_index: currentIndex,
          total_items: content.length
        });
      } catch (error) {
        console.error('Health check-in failed:', error);
      }
    };
    
    sendHeartbeat();
    const interval = setInterval(sendHeartbeat, 2 * 60 * 1000);
    return () => clearInterval(interval);
  }, [displayId, currentIndex, content.length]);
  
  // Load content
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const statusResponse = await axios.get(`${API}/admin/page-status/bkrmmn`);
        if (!statusResponse.data.enabled) {
          setPageEnabled(false);
          setLoading(false);
          return;
        }
        setOrientation(statusResponse.data.orientation || 'landscape');
        setWeatherZipCode(statusResponse.data.weather_zip_code || null);
        const isWeatherEnabled = statusResponse.data.weather_enabled !== false;
        setWeatherEnabled(isWeatherEnabled);
        setSlideDuration(statusResponse.data.slide_duration || 5);
        const adEnabled = statusResponse.data.show_default_ad !== false;
        setShowDefaultAd(adEnabled);
        setAlertText(statusResponse.data.alert_text || null);
        setAlertEnabled(statusResponse.data.alert_enabled || false);
        setZoneLayout(statusResponse.data.zone_layout || false);
        setZoneClock(statusResponse.data.zone_clock !== false);
        setZoneTicker(statusResponse.data.zone_ticker || '');
        
        try {
          const playlistResponse = await axios.get(`${API}/pages/bkrmmn/current-playlist`);
          if (playlistResponse.data.use_playlists && playlistResponse.data.items?.length > 0) {
            setContent([...getDefaultContent(isWeatherEnabled, adEnabled), ...playlistResponse.data.items]);
            setLoading(false);
            return;
          }
        } catch (playlistError) {
          console.log('Playlist fetch failed, falling back to default content');
        }
        
        const response = await axios.get(`${API}/bkrmmn-content`);
        const items = response.data.items || [];
        setContent([...getDefaultContent(isWeatherEnabled, adEnabled), ...items]);
      } catch (error) {
        console.error('Failed to load content:', error);
        setContent([...getDefaultContent()]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
    const contentInterval = setInterval(fetchContent, 5 * 60 * 1000);
    return () => clearInterval(contentInterval);
  }, []);
  
  const goToNext = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % content.length);
    setImageProgress(0);
  }, [content.length]);
  
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + content.length) % content.length);
    setImageProgress(0);
  }, [content.length]);
  
  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (showVideo) return;
      switch (e.key) {
        case 'ArrowRight':
        case ' ':
          e.preventDefault();
          goToNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          goToPrevious();
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showVideo, goToNext, goToPrevious]);
  
  // Preload next media
  useEffect(() => {
    if (content.length <= 1) return;
    const nextIndex = (currentIndex + 1) % content.length;
    const nextItem = content[nextIndex];
    if (nextItem?.type === 'image') {
      const img = new Image();
      img.src = nextItem.url;
    }
  }, [currentIndex, content]);
  
  // Auto-advance slideshow
  useEffect(() => {
    if (content.length === 0 || showVideo) return;
    const currentItem = content[currentIndex];
    
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
    
    if (currentItem?.type === 'image' || currentItem?.type === 'weather') {
      setImageProgress(0);
      const duration = (currentItem.duration || slideDuration) * 1000;
      const interval = 50;
      let elapsed = 0;
      
      progressIntervalRef.current = setInterval(() => {
        elapsed += interval;
        setImageProgress((elapsed / duration) * 100);
        if (elapsed >= duration) {
          clearInterval(progressIntervalRef.current);
          goToNext();
        }
      }, interval);
      
      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    }
    
    if (currentItem?.type === 'video' && isYouTubeUrl(currentItem.url)) {
      setImageProgress(0);
      const timer = setTimeout(() => {
        goToNext();
      }, 120000);
      return () => clearTimeout(timer);
    }
    
    setImageProgress(0);
  }, [currentIndex, content, showVideo, goToNext]);
  
  const handleVideoEnded = useCallback(() => {
    if (pendingRefresh) {
      window.location.reload();
      return;
    }
    goToNext();
  }, [goToNext, pendingRefresh]);
  
  const handleClick = () => {
    setShowVideo(true);
    setTimeout(() => {
      if (videoContainerRef.current?.requestFullscreen) {
        videoContainerRef.current.requestFullscreen().catch(err => console.log('Fullscreen failed:', err));
      }
    }, 100);
  };
  
  const handleBackToSlideshow = () => {
    setShowVideo(false);
    if (document.exitFullscreen) {
      document.exitFullscreen().catch(err => console.log('Exit fullscreen failed:', err));
    }
  };
  
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreen = !!document.fullscreenElement;
      if (!isFullscreen && showVideo) {
        setShowVideo(false);
      }
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [showVideo]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-xl">Loading content...</p>
        </div>
      </div>
    );
  }
  
  if (!pageEnabled) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">This page is currently disabled</p>
        </div>
      </div>
    );
  }
  
  if (content.length === 0) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white text-xl mb-4">No content available</p>
        </div>
      </div>
    );
  }
  
  const portraitStyles = orientation === 'portrait' ? {
    transform: 'rotate(90deg)',
    transformOrigin: 'center center',
    width: '100vh',
    height: '100vw',
    position: 'fixed',
    top: '50%',
    left: '50%',
    marginTop: '-50vw',
    marginLeft: '-50vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  } : {};
  
  // Content styles - ensure images/videos fit properly without cropping
  const contentFitClass = 'object-contain';
  
  return (
    <div className="min-h-screen bg-black flex items-center justify-center overflow-hidden">
      <div className="w-full h-screen" style={portraitStyles}>
        {zoneLayout ? (
          <ZoneLayout ticker={zoneTicker} showClock={zoneClock}>
            <div ref={videoContainerRef} className="relative bg-black w-full h-full">
              {videoLoading && (<div className="absolute inset-0 z-25 flex items-center justify-center bg-black/50"><div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div></div>)}
              {!showVideo && (
                <div className="absolute inset-0 z-20 cursor-pointer" onClick={handleClick}>
                  {content.map((item, index) => (
                    <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                      {item.type === 'weather' ? (index === currentIndex && <WeatherSlide isPortrait={orientation === 'portrait'} zipCode={weatherZipCode} />)
                      : item.type === 'image' ? (<div className="absolute inset-0 flex items-center justify-center bg-black"><img src={getFullUrl(item.url)} alt={`Content ${index + 1}`} className="max-w-full max-h-full object-contain" /></div>)
                      : isYouTubeUrl(item.url) ? (index === currentIndex && <iframe src={`${getYouTubeEmbedUrl(item.url)}?autoplay=1&controls=0&enablejsapi=1`} title={`Video ${index + 1}`} allow="accelerometer; autoplay" allowFullScreen className="w-full h-full" style={{ border: 'none', pointerEvents: 'none' }} />)
                      : (index === currentIndex && <video key={`video-${index}`} src={getFullUrl(item.url)} autoPlay playsInline className={`w-full h-full ${contentFitClass} bg-black`} onEnded={handleVideoEnded} onLoadStart={() => setVideoLoading(true)} onCanPlay={() => setVideoLoading(false)} onError={() => { setVideoLoading(false); handleVideoEnded(); }} />)}
                    </div>
                  ))}
                </div>
              )}
              {showVideo && (<>
                {content[currentIndex]?.type === 'image' ? (<img src={getFullUrl(content[currentIndex].url)} alt="Fullscreen" className={`absolute inset-0 w-full h-full ${contentFitClass}`} />)
                : isYouTubeUrl(content[currentIndex]?.url) ? (<iframe src={`${getYouTubeEmbedUrl(content[currentIndex]?.url)}?autoplay=1&controls=1`} title="Fullscreen Video" allow="accelerometer; autoplay" allowFullScreen className="absolute inset-0 w-full h-full" style={{ border: 'none' }} />)
                : (<video src={getFullUrl(content[currentIndex]?.url)} autoPlay controls playsInline className={`absolute inset-0 w-full h-full ${contentFitClass} bg-black`} />)}
                <button onClick={handleBackToSlideshow} className="absolute top-4 right-4 z-30 bg-slate-900/80 hover:bg-slate-800 text-white px-4 py-2 rounded-lg">Back to Slideshow</button>
              </>)}
            </div>
          </ZoneLayout>
        ) : (
          <div ref={videoContainerRef} className="relative bg-black w-full h-full">
            {videoLoading && (<div className="absolute inset-0 z-25 flex items-center justify-center bg-black/50"><div className="w-12 h-12 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin"></div></div>)}
            {!showVideo && (
              <div className="absolute inset-0 z-20 cursor-pointer" onClick={handleClick}>
                {content.map((item, index) => (
                  <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${index === currentIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                    {item.type === 'weather' ? (index === currentIndex && <WeatherSlide isPortrait={orientation === 'portrait'} zipCode={weatherZipCode} />)
                    : item.type === 'image' ? (<div className="absolute inset-0 flex items-center justify-center bg-black"><img src={getFullUrl(item.url)} alt={`Content ${index + 1}`} className="max-w-full max-h-full object-contain" /></div>)
                    : isYouTubeUrl(item.url) ? (index === currentIndex && <iframe src={`${getYouTubeEmbedUrl(item.url)}?autoplay=1&controls=0&enablejsapi=1`} title={`Video ${index + 1}`} allow="accelerometer; autoplay" allowFullScreen className="w-full h-full" style={{ border: 'none', pointerEvents: 'none' }} />)
                    : (index === currentIndex && <video key={`video-${index}`} src={getFullUrl(item.url)} autoPlay playsInline className={`w-full h-full ${contentFitClass} bg-black`} onEnded={handleVideoEnded} onLoadStart={() => setVideoLoading(true)} onCanPlay={() => setVideoLoading(false)} onError={() => { setVideoLoading(false); handleVideoEnded(); }} />)}
                  </div>
                ))}
              </div>
            )}
            {showVideo && (<>
              {content[currentIndex]?.type === 'image' ? (<img src={getFullUrl(content[currentIndex].url)} alt="Fullscreen" className={`absolute inset-0 w-full h-full ${contentFitClass}`} />)
              : isYouTubeUrl(content[currentIndex]?.url) ? (<iframe src={`${getYouTubeEmbedUrl(content[currentIndex]?.url)}?autoplay=1&controls=1`} title="Fullscreen Video" allow="accelerometer; autoplay" allowFullScreen className="absolute inset-0 w-full h-full" style={{ border: 'none' }} />)
              : (<video src={getFullUrl(content[currentIndex]?.url)} autoPlay controls playsInline className={`absolute inset-0 w-full h-full ${contentFitClass} bg-black`} />)}
              <button onClick={handleBackToSlideshow} className="absolute top-4 right-4 z-30 bg-slate-900/80 hover:bg-slate-800 text-white px-4 py-2 rounded-lg">Back to Slideshow</button>
            </>)}
          </div>
        )}
      </div>
      <AlertBar text={alertText} enabled={alertEnabled} />
    </div>
  );
};

export default BkrmmnPage;
